import { useEffect, useState } from 'react'

/*
 * Разворачивает плитку (ближайший элемент с data-user-id) на весь экран.
 *
 * Именно плитку, а не сам <video>: у видеоэлемента, ушедшего в полный экран, Chromium
 * рисует собственные кнопки — пауза, таймер, громкость, — которые к трансляции никакого
 * отношения не имеют. Хуже того, они показывают «звук выключен»: звук у нас идёт
 * отдельным элементом, а у видеодорожки его нет и быть не может. У обычного контейнера
 * таких кнопок нет, а видео внутри растягивается стилями.
 *
 * На iPhone обычный элемент на весь экран не разворачивается вовсе — у него нет ни
 * requestFullscreen, ни webkitRequestFullscreen, кнопка просто молчала. Там единственный
 * путь — родной плеер самого <video> через webkitEnterFullscreen: с его кнопками, зато
 * с поворотом и во весь экран. Звук трансляции при этом продолжает идти своим элементом.
 */
type FullscreenVideo = HTMLVideoElement & { webkitEnterFullscreen?: () => void; webkitDisplayingFullscreen?: boolean }

export function fullscreenTileVideo(e: { currentTarget: Element }): void {
  const tile = e.currentTarget.closest('[data-user-id]') as
    | (HTMLElement & { webkitRequestFullscreen?: () => void })
    | null
  const video = tile?.querySelector('video') as FullscreenVideo | null
  if (!tile || !video) return
  if (document.fullscreenElement) {
    void document.exitFullscreen().catch(() => {})
    return
  }
  if (tile.requestFullscreen) void tile.requestFullscreen().catch(() => {})
  else if (tile.webkitRequestFullscreen) tile.webkitRequestFullscreen()
  else if (video.webkitEnterFullscreen && !video.webkitDisplayingFullscreen) video.webkitEnterFullscreen()
}

/**
 * Развернуть на весь экран элемент целиком (просмотр трансляции с панелями) или свернуть обратно.
 * На iPhone так нельзя — там, как и у плитки, остаётся родной плеер первого видео внутри.
 */
export function toggleElementFullscreen(el: HTMLElement): void {
  if (document.fullscreenElement) {
    void document.exitFullscreen().catch(() => {})
    return
  }
  const box = el as HTMLElement & { webkitRequestFullscreen?: () => void }
  const video = el.querySelector('video') as FullscreenVideo | null
  if (box.requestFullscreen) void box.requestFullscreen().catch(() => {})
  else if (box.webkitRequestFullscreen) box.webkitRequestFullscreen()
  else if (video?.webkitEnterFullscreen && !video.webkitDisplayingFullscreen) video.webkitEnterFullscreen()
}

/*
 * В полноэкранном просмотре курсор и накладные кнопки мешают: через пару секунд без движения
 * мыши плитка получает data-idle, стили прячут курсор и кнопки, любое движение возвращает.
 * Ставится один раз на документ и работает для любой развёрнутой плитки.
 */
const IDLE_MS = 2500

let idleTimer: ReturnType<typeof setTimeout> | null = null
let idleTarget: HTMLElement | null = null

const wake = () => {
  if (!idleTarget) return
  idleTarget.removeAttribute('data-idle')
  if (idleTimer) clearTimeout(idleTimer)
  idleTimer = setTimeout(() => idleTarget?.setAttribute('data-idle', ''), IDLE_MS)
}

const stopIdle = () => {
  if (idleTimer) clearTimeout(idleTimer)
  idleTimer = null
  if (idleTarget) {
    idleTarget.removeAttribute('data-idle')
    idleTarget.removeEventListener('mousemove', wake)
    idleTarget.removeEventListener('mousedown', wake)
    idleTarget.removeEventListener('keydown', wake)
  }
  idleTarget = null
}

export function installFullscreenIdleCursor(): void {
  if (typeof document === 'undefined') return
  document.addEventListener('fullscreenchange', () => {
    stopIdle()
    const el = document.fullscreenElement as HTMLElement | null
    if (!el || !el.hasAttribute('data-user-id')) return
    idleTarget = el
    el.addEventListener('mousemove', wake)
    el.addEventListener('mousedown', wake)
    el.addEventListener('keydown', wake)
    wake()
  })
}

installFullscreenIdleCursor()

/*
 * Esc выводит из полного экрана и в приложении для компьютера: в браузере это делает он сам, а Electron
 * отдаёт клавишу странице — из развёрнутой камеры было не выйти. Ловим раньше всех, чтобы тот же Esc
 * не закрыл заодно окно или настройки под развёрнутой плиткой.
 */
export function installFullscreenEscape(): void {
  if (typeof document === 'undefined') return
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !document.fullscreenElement) return
    e.preventDefault()
    e.stopPropagation()
    void document.exitFullscreen().catch(() => {})
  }, true)
}

installFullscreenEscape()

/** Развёрнутый на весь экран элемент, если он есть; следит за сменой. */
export function useFullscreenHost(): Element | null {
  const [host, setHost] = useState<Element | null>(() => (typeof document === 'undefined' ? null : document.fullscreenElement))
  useEffect(() => {
    const on = () => setHost(document.fullscreenElement)
    document.addEventListener('fullscreenchange', on)
    return () => document.removeEventListener('fullscreenchange', on)
  }, [])
  return host
}
